import type {
  CashierAdapter,
  CashierApplePayReadiness,
  CashierCryptoAsset,
  CashierFlow,
  CashierMethodView,
} from "../types";
import { canonicalMethodId, paymentMethodDefinition } from "./registry";

export type PaymentMethodRuntimeState = {
  actionable: boolean;
  reason: string | null;
  status: "checking" | "ready" | "unavailable";
};

function advertisedReason(method: CashierMethodView): string | null {
  return method.unavailableReason ?? method.detail ?? null;
}

export function paymentMethodRuntimeState({
  adapter,
  applePay,
  cryptoAssets,
  flow,
  method,
}: {
  adapter: CashierAdapter | null;
  applePay: CashierApplePayReadiness;
  cryptoAssets: readonly CashierCryptoAsset[];
  flow: CashierFlow;
  method: CashierMethodView;
}): PaymentMethodRuntimeState {
  const canonical = canonicalMethodId(method.id);
  const hasAppleCallback =
    typeof adapter?.applePay?.beginSession === "function";
  const appleReady =
    canonical === "apple_pay" && applePay.state === "ready" && hasAppleCallback;

  if (
    canonical === "apple_pay" &&
    method.availability === "checking" &&
    !appleReady
  ) {
    return applePay.state === "checking" && hasAppleCallback
      ? {
          actionable: false,
          reason: advertisedReason(method) ?? "Checking availability…",
          status: "checking",
        }
      : {
          actionable: false,
          reason: applePay.message ?? advertisedReason(method) ?? "Unavailable",
          status: "unavailable",
        };
  }

  if (method.availability === "checking" && !appleReady) {
    return {
      actionable: false,
      reason: advertisedReason(method) ?? "Checking availability…",
      status: "checking",
    };
  }

  if (method.availability !== "ready" && !appleReady) {
    return {
      actionable: false,
      reason:
        advertisedReason(method) ??
        (method.availability === "coming-soon" ? "Coming soon" : "Unavailable"),
      status: "unavailable",
    };
  }

  const definition = paymentMethodDefinition(method.id);
  if (!adapter || !canonical || !definition?.flows.includes(flow)) {
    return {
      actionable: false,
      reason: "Unavailable",
      status: "unavailable",
    };
  }

  const hasEnabledNetwork = cryptoAssets.some((asset) =>
    flow === "deposit" ? asset.depositEnabled : asset.withdrawEnabled,
  );
  const hasAction =
    canonical === "apple_pay"
      ? flow === "deposit"
        ? applePay.state === "ready" &&
          typeof adapter.applePay?.beginSession === "function"
        : typeof adapter.beginWithdraw === "function"
      : canonical === "card"
        ? flow === "deposit"
          ? typeof adapter.beginDeposit === "function"
          : typeof adapter.beginWithdraw === "function"
        : canonical === "crypto"
          ? hasEnabledNetwork &&
            (flow === "deposit"
              ? typeof adapter.getCryptoDepositAddress === "function"
              : typeof adapter.cryptoWithdraw === "function")
          : flow === "deposit"
            ? typeof adapter.beginDepositMethod === "function"
            : typeof adapter.beginWithdraw === "function";

  return hasAction
    ? { actionable: true, reason: null, status: "ready" }
    : {
        actionable: false,
        reason: advertisedReason(method) ?? "Unavailable",
        status: "unavailable",
      };
}
